import React, { useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useSceneContext } from '../contexts/SceneContext';
import { createMaterials } from '../utils/materials';
import { createConveyorSystem } from '../utils/conveyorSystem';
import { createPCBs } from '../utils/pcbs';
import { createRobotArms } from '../utils/robotArms';
import { createDrone } from '../utils/drone';
import { createFrameConveyor } from '../utils/frameConveyor';
import { createParticles } from '../utils/particles';

interface Scene3DProps {
    mountRef: React.RefObject<HTMLDivElement | null>;
}

const Scene3D: React.FC<Scene3DProps> = ({ mountRef }) => {
    const {
        sceneRef,
        rendererRef,
        cameraRef,
        droneCameraRef,
        controlsRef,
        componentsRef,
        particlesRef,
        droneRef,
        framePartsRef,
        animationFrameId
    } = useSceneContext();

    // 初始化 Three.js 场景
    const initScene = useCallback(() => {
        if (!mountRef.current) {
            console.error("Mount reference is not attached.");
            return;
        }

        // 获取容器尺寸
        const width = mountRef.current.clientWidth;
        const height = mountRef.current.clientHeight;

        // 创建场景
        const scene = new THREE.Scene();
        sceneRef.current = scene;
        scene.background = new THREE.Color(0x0e172a); // 深蓝色背景，科技感

        // 创建主摄像机
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        camera.position.set(0, 10, 20);
        cameraRef.current = camera;

        // 创建渲染器
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        rendererRef.current = renderer;
        
        // 确保清除旧的 canvas 元素，避免重复添加
        if (mountRef.current.firstChild) {
            mountRef.current.removeChild(mountRef.current.firstChild);
        }
        mountRef.current.appendChild(renderer.domElement);

        // 添加灯光
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
        directionalLight.position.set(10, 20, 10);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        // 添加轨道控制器
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controlsRef.current = controls;

        // 清空之前的部件引用
        componentsRef.current = [];

        // 创建材质
        const materials = createMaterials();

        // 创建各个系统
        createConveyorSystem(scene, materials, componentsRef);
        createPCBs(scene, materials, componentsRef);
        createRobotArms(scene, materials, componentsRef);
        createDrone(scene, materials, componentsRef, droneRef, droneCameraRef);
        createFrameConveyor(scene, materials, componentsRef, framePartsRef);
        createParticles(scene, particlesRef);

        // 处理窗口大小调整
        const handleResize = () => {
            if (!mountRef.current) return;
            const newWidth = mountRef.current.clientWidth;
            const newHeight = mountRef.current.clientHeight;
            
            camera.aspect = newWidth / newHeight;
            camera.updateProjectionMatrix();

            if (droneCameraRef.current) {
                droneCameraRef.current.aspect = 1;
                droneCameraRef.current.updateProjectionMatrix();
            }

            renderer.setSize(newWidth, newHeight);
        };
        window.addEventListener('resize', handleResize);

        // 清理函数
        return () => {
            window.removeEventListener('resize', handleResize);
            if (mountRef.current && renderer.domElement) {
                const canvas = mountRef.current.querySelector('canvas');
                if (canvas) {
                    mountRef.current.removeChild(canvas);
                }
            }
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            if (controls) controls.dispose();
            if (renderer) renderer.dispose();
            if (scene) {
                scene.traverse((obj: THREE.Object3D) => {
                    const mesh = obj as THREE.Mesh;
                    if (mesh.geometry) mesh.geometry.dispose();
                    if (mesh.material) {
                        if (Array.isArray(mesh.material)) {
                            mesh.material.forEach((mat: THREE.Material) => mat.dispose());
                        } else {
                            (mesh.material as THREE.Material).dispose();
                        }
                    }
                });
            }
        };
    }, [mountRef, sceneRef, rendererRef, cameraRef, droneCameraRef, controlsRef, componentsRef, particlesRef, droneRef, framePartsRef, animationFrameId]);

    useEffect(() => {
        const cleanup = initScene();
        return cleanup;
    }, [initScene]);

    return null;
};

export default Scene3D; 